import { useCallback, useMemo, useState } from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';

import { FProgressBar } from '../../../../../../components/base';
import CollapseIconBordered from '../../../../../../components/svgs/CollapseIconBordered';
import { DATE_DISPLAY_FORMAT } from '../../../../../../constants';
import { disabledFutureDate } from '../../../../../../helpers';
import { FORTA_ERROR, FORTA_WHITE } from '../../../../../../utils/colors';
import { FormDateField, FormFieldReadOnlyValue, useFormReadOnly } from '../../../../../common';
import { ASSESSMENT_READINESS_SECTIONS } from '../constants';
import { useSaveTreatmentPlanV3PageField } from '../../../../hooks';
import { getAssessmentReadinessDateCompareValue } from '../hooks';
import {
  ASSESSMENT_READINESS_CARD_SX,
  ASSESSMENT_READINESS_DATE_FIELD_SX,
  ASSESSMENT_READINESS_DETAILS_SX,
  ASSESSMENT_READINESS_HEADER_FIELDS_SX,
  ASSESSMENT_READINESS_HEADER_SX,
  ASSESSMENT_READINESS_OVERALL_SCORE_FIELD_SX,
  getAssessmentReadinessExpandIconSx,
} from '../styles';
import {
  formatAssessmentReadinessOverallScore,
  hasAssessmentReadinessErrors,
  mapAssessmentPageToReadinessDateValues,
  toAssessmentReadinessDatePayload,
} from '../utils';
import { ASSESSMENT_READINESS_DATE_FORM_SCHEMA } from '../validationSchema';
import AssessmentReadinessNeededCheckbox from './AssessmentReadinessNeededCheckbox';
import AssessmentReadinessSubsection from './AssessmentReadinessSubsection';
import { TREATMENT_PLAN_V3_SECTION_HEADING_SX } from '../../../../styles';

import type { AssessmentReadinessSectionProps } from './types';

export default function AssessmentReadinessSection({
  assessmentPage,
  disabled,
  isSaving,
  onUpdatePage,
  onUpdateItem,
}: AssessmentReadinessSectionProps) {
  const readOnly = useFormReadOnly();
  const needed = assessmentPage.telehealth_readiness_assessment_needed;
  const [expanded, setExpanded] = useState(true);
  const initialValues = useMemo(
    () => mapAssessmentPageToReadinessDateValues(assessmentPage),
    [assessmentPage],
  );
  const formik = useFormik({
    initialValues,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: ASSESSMENT_READINESS_DATE_FORM_SCHEMA,
    onSubmit: () => undefined,
  });
  const { saveField } = useSaveTreatmentPlanV3PageField({
    formik,
    getCompareValue: getAssessmentReadinessDateCompareValue,
    initialValues,
    onUpdate: onUpdatePage,
    toPayload: toAssessmentReadinessDatePayload,
    validationSchema: ASSESSMENT_READINESS_DATE_FORM_SCHEMA,
  });
  const handleNeededChange = useCallback(
    (checked: boolean) => {
      void onUpdatePage({ telehealth_readiness_assessment_needed: checked });
    },
    [onUpdatePage],
  );
  const handleToggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <Stack gap={2}>
      <Typography variant="h5" sx={TREATMENT_PLAN_V3_SECTION_HEADING_SX}>
        Telehealth Readiness Assessment
      </Typography>
      <AssessmentReadinessNeededCheckbox
        checked={needed}
        disabled={disabled || isSaving}
        onChange={handleNeededChange}
      />
      {needed ? (
        <Box sx={ASSESSMENT_READINESS_CARD_SX}>
          {isSaving ? <FProgressBar /> : null}
          <Stack>
            <Stack sx={ASSESSMENT_READINESS_HEADER_SX}>
              <Stack direction="row" alignItems="center" gap={1}>
                {!expanded && hasAssessmentReadinessErrors(assessmentPage, formik.values) ? (
                  <ErrorIcon
                    role="img"
                    aria-label="Readiness Assessment has errors"
                    sx={{ color: FORTA_ERROR, fontSize: 16 }}
                  />
                ) : null}
                <Typography variant="h6" sx={TREATMENT_PLAN_V3_SECTION_HEADING_SX}>
                  Readiness Assessment
                </Typography>
              </Stack>
              <Stack sx={ASSESSMENT_READINESS_HEADER_FIELDS_SX}>
                <Box sx={ASSESSMENT_READINESS_OVERALL_SCORE_FIELD_SX}>
                  <FormFieldReadOnlyValue
                    label="Overall Score"
                    value={formatAssessmentReadinessOverallScore(
                      assessmentPage.readiness_scores?.overall ?? null,
                    )}
                  />
                </Box>
                <Box sx={ASSESSMENT_READINESS_DATE_FIELD_SX}>
                  <FormDateField
                    disabled={disabled}
                    disabledDate={disabledFutureDate}
                    formik={formik}
                    format={DATE_DISPLAY_FORMAT}
                    label="Assessment date"
                    name="telehealth_assessment_date"
                    onChange={saveField}
                    placeholder="Select date"
                    readOnly={readOnly}
                    required
                  />
                </Box>
                <IconButton
                  aria-expanded={expanded}
                  aria-label={
                    expanded ? 'Collapse Readiness Assessment' : 'Expand Readiness Assessment'
                  }
                  onClick={handleToggleExpanded}
                  size="small"
                  sx={getAssessmentReadinessExpandIconSx(expanded)}
                >
                  <CollapseIconBordered
                    color={expanded ? FORTA_WHITE : undefined}
                    height={36}
                    width={36}
                  />
                </IconButton>
              </Stack>
            </Stack>
            <Collapse in={expanded} timeout="auto">
              <Stack sx={ASSESSMENT_READINESS_DETAILS_SX}>
                {ASSESSMENT_READINESS_SECTIONS.map((section) => (
                  <AssessmentReadinessSubsection
                    key={section.id}
                    disabled={disabled}
                    items={section.items}
                    onUpdateItem={onUpdateItem}
                    readinessItems={assessmentPage.readiness_items ?? []}
                    sectionScore={assessmentPage.readiness_scores?.[section.id] ?? null}
                    title={section.title}
                  />
                ))}
              </Stack>
            </Collapse>
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
}
